import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { HorizontalLoader } from 'Components'
import ModalWrapper from 'Components/ModalWrapper/ModalWrapper'
import { handleError, handleSuccess } from 'Helpers'
import './PriceMatchDetails.scss'
import backArrow from '../../../../../Assets/Icon/Users/back-arrow-detailed.svg'
import breadcrumbArrow from '../../../../../Assets/Icon/Referrals/breadcrumb-arrow.svg'
import { ToastContainer } from 'react-toastify'
import { priceMatchBXService } from 'BoostXpressAdmin/Services/priceMatchBXService'
import {
  getPriceMatchStatusLabel,
  mapPriceMatchSubmission,
  PriceMatchSubmission,
} from './priceMatchData'
import fillingStationService from 'Services/fillingStationService'
import { FillingStationType } from 'Models/fillingStationType'

const rejectionOptions = [
  'Image is not Clear',
  'Station name not visible',
  'Fuel price not visible',
  'Location mismatch',
]

const formatReviewDate = (date: Date) => {
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  return `${datePart} · ${timePart}`
}

const PriceMatchPreviewGraphic = () => (
  <svg
    viewBox='0 0 800 760'
    className='bx-price-match-preview__svg'
    xmlns='http://www.w3.org/2000/svg'
  >
    <defs>
      <linearGradient id='skyGradient' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stopColor='#0e8fff' />
        <stop offset='100%' stopColor='#dbeeff' />
      </linearGradient>
      <linearGradient id='boardGradient' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stopColor='#0a7f4e' />
        <stop offset='100%' stopColor='#045733' />
      </linearGradient>
      <linearGradient id='roofGradient' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stopColor='#8fd25b' />
        <stop offset='100%' stopColor='#2f7d2b' />
      </linearGradient>
      <filter id='shadow' x='-20%' y='-20%' width='140%' height='140%'>
        <feDropShadow dx='0' dy='12' stdDeviation='12' floodColor='#16314d' floodOpacity='0.25' />
      </filter>
    </defs>

    <rect width='800' height='760' fill='url(#skyGradient)' />
    <ellipse cx='128' cy='646' rx='210' ry='44' fill='#74a14d' opacity='0.55' />
    <ellipse cx='650' cy='660' rx='220' ry='48' fill='#6c9150' opacity='0.45' />
    <path d='M0 585L110 560L240 562L240 760L0 760Z' fill='url(#roofGradient)' />
    <path d='M0 624L250 624L250 652L0 680Z' fill='#ece7df' />
    <path d='M536 702L594 700L612 760L520 760Z' fill='#e5ddd5' />
    <rect x='350' y='640' width='110' height='32' rx='10' fill='#5f5d58' />
    <rect x='380' y='328' width='52' height='324' rx='18' fill='#ddd8d2' />
    <rect x='392' y='326' width='28' height='324' rx='14' fill='#f7f4ef' />
    <g filter='url(#shadow)' transform='translate(238 38) rotate(-4 160 170)'>
      <rect x='130' y='64' width='260' height='364' rx='18' fill='#376a3d' />
      <rect x='142' y='76' width='236' height='340' rx='14' fill='url(#boardGradient)' />
      <rect x='150' y='86' width='220' height='44' rx='9' fill='#ebf7ef' />
      <rect x='164' y='93' width='34' height='30' rx='15' fill='#197a47' />
      <path d='M174 108L185 98L182 108H194L179 118L183 108H174Z' fill='#f3e851' />
      <text
        x='212'
        y='116'
        fill='#006942'
        fontSize='30'
        fontFamily='Poppins, sans-serif'
        fontWeight='700'
      >
        FUEL
      </text>
      <rect x='300' y='95' width='62' height='24' rx='7' fill='#d9ded9' />
      <text
        x='308'
        y='112'
        fill='#617062'
        fontSize='14'
        fontFamily='Poppins, sans-serif'
        fontWeight='600'
      >
        STATION
      </text>

      {[
        { y: 148, label: 'PMS', value: '1,300' },
        { y: 207, label: 'AGO', value: '1,800' },
        { y: 266, label: 'LPG', value: '1,200' },
        { y: 325, label: 'DPK', value: '1,150' },
        { y: 384, label: 'CNG', value: '850' },
      ].map((row) => (
        <g key={row.label}>
          <rect
            x='154'
            y={row.y}
            width='210'
            height='48'
            rx='8'
            fill='#093f30'
            stroke='#49765c'
            strokeWidth='2'
          />
          <text
            x='172'
            y={row.y + 30}
            fill='#d2f6cc'
            fontSize='28'
            fontFamily='Poppins, sans-serif'
            fontWeight='700'
          >
            {row.label}
          </text>
          <text
            x='262'
            y={row.y + 31}
            fill='#5af27c'
            fontSize='34'
            fontFamily='monospace'
            fontWeight='700'
            letterSpacing='2'
          >
            {row.value}
          </text>
        </g>
      ))}
    </g>
    <circle cx='105' cy='184' r='30' fill='#ffffff' opacity='0.72' />
    <circle cx='152' cy='171' r='24' fill='#ffffff' opacity='0.72' />
    <circle cx='695' cy='128' r='28' fill='#ffffff' opacity='0.64' />
    <circle cx='732' cy='140' r='22' fill='#ffffff' opacity='0.64' />
  </svg>
)

const PriceMatchDetails = () => {
  const navigate = useNavigate()
  const { submissionId } = useParams()

  const [submission, setSubmission] = useState<PriceMatchSubmission | null>(null)
  const [station, setStation] = useState<FillingStationType | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [approvePriceDifferenceMajor, setApprovePriceDifferenceMajor] = useState<string>('')
  const [rejectionReason, setRejectionReason] = useState(rejectionOptions[0])
  const [reviewNotes, setReviewNotes] = useState(
    'Fuel price was partially obstructed in uploaded image.',
  )
  const [showBanner, setShowBanner] = useState(false)
  const [previewScale, setPreviewScale] = useState(1)
  const [previewRotation, setPreviewRotation] = useState(0)
  const previewRef = useRef<HTMLDivElement | null>(null)

  const loadSubmission = async () => {
    if (!submissionId) return

    try {
      setIsLoading(true)
      const res = await priceMatchBXService.show(submissionId)
      const payload = res.data?.data ?? null
      console.log({ payload })
      setSubmission(payload ? mapPriceMatchSubmission(payload) : null)
    } catch (error) {
      setSubmission(null)
      handleError({ response: { data: { error: 'Failed to fetch price match details.' } } })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSubmission()
  }, [submissionId])

  useEffect(() => {
    if (!submission) return
    setRejectionReason(submission.rejectionReason ?? rejectionOptions[0])
    setReviewNotes(
      submission.reviewNotes ?? 'Fuel price was partially obstructed in uploaded image.',
    )
    setShowBanner(submission.status !== 'PENDING')
  }, [submission])

  useEffect(() => {
    if (!showApproveModal) return
    setApprovePriceDifferenceMajor(
      submission?.priceDifferenceMajor !== undefined && submission?.priceDifferenceMajor !== null
        ? String(submission.priceDifferenceMajor)
        : '',
    )
  }, [showApproveModal, submission?.priceDifferenceMajor])

  useEffect(() => {
    let cancelled = false

    const getStation = async () => {
      if (!submission?.order?.stationId) {
        setStation(null)
        return
      }
      try {
        const response = await fillingStationService.getById(submission.order?.stationId)
        if (!cancelled) {
          console.log({ response })
          setStation(response.data?.data ?? null)
        }
      } catch (error) {
        if (!cancelled) {
          setStation(null)
          handleError({ response: { data: { error: 'Failed to fetch station details.' } } })
        }
      }
    }

    getStation()

    return () => {
      cancelled = true
    }
  }, [submission?.order?.stationId])

  const previewStyle = useMemo<CSSProperties>(
    () => ({
      transform: `scale(${previewScale}) rotate(${previewRotation}deg)`,
    }),
    [previewRotation, previewScale],
  )

  const handleApprove = async () => {
    if (!submissionId) return

    const priceDifferenceMajor = Number(approvePriceDifferenceMajor)
    if (Number.isNaN(priceDifferenceMajor) || priceDifferenceMajor < 0) {
      handleError({ response: { data: { error: 'Enter a valid price differential amount.' } } })
      return
    }

    try {
      setIsSubmitting(true)
      await priceMatchBXService.approve(submissionId, {
        priceDifferenceMajor,
      })
      setShowApproveModal(false)
      await loadSubmission()
      setShowBanner(true)
      handleSuccess('Price match approved successfully.')
    } catch (error) {
      handleError({ response: { data: { error: 'Failed to approve price match.' } } })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!submissionId) return

    const nextReviewNotes = reviewNotes.trim()
    if (!nextReviewNotes) {
      handleError({ response: { data: { error: 'Rejection note is required.' } } })
      return
    }

    try {
      setIsSubmitting(true)
      await priceMatchBXService.reject(submissionId, {
        rejectionNote: `${rejectionReason} || ${nextReviewNotes}`,
      })
      setShowRejectModal(false)
      await loadSubmission()
      setShowBanner(true)
      handleSuccess('Price match rejected successfully.')
    } catch (error) {
      handleError({ response: { data: { error: 'Failed to reject price match.' } } })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className='bx-price-match-review'>
        <ToastContainer />
        <div className='bx-price-match-review__loading'>
          <HorizontalLoader />
        </div>
      </div>
    )
  }

  if (!submission) {
    return (
      <div className='bx-price-match-review'>
        <ToastContainer />
        <div className='bx-price-match-review__empty'>Price match submission not found.</div>
      </div>
    )
  }

  const isPending = submission.status === 'PENDING'
  const isApproved = submission.status === 'APPROVED'
  const isDeclined = submission.status === 'DECLINED'

  const downloadPreview = () => {
    if (submission.imageUrl) {
      const anchor = document.createElement('a')
      anchor.href = submission.imageUrl
      anchor.download = `${submission.orderId.toLowerCase()}-price-match`
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
      anchor.click()
      return
    }

    const svgElement = previewRef.current?.querySelector('svg')
    if (!svgElement) return

    const serializedSvg = new XMLSerializer().serializeToString(svgElement)
    const blob = new Blob([serializedSvg], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${submission.orderId.toLowerCase()}-price-match.svg`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className='bx-price-match-review'>
      <ToastContainer />
      <div className='bx-price-match-review__breadcrumb'>
        <button type='button' className='bx-price-match-review__back' onClick={() => navigate(-1)}>
          <img src={backArrow} alt='Back' />
        </button>
        <div className='bx-price-match-review__crumbs'>
          <Link to='/boostxpress/price-match' className='bx-price-match-review__crumb-link'>
            Price Match
          </Link>
          <img src={breadcrumbArrow} alt='' />
          <span className='bx-price-match-review__crumb-current'>Review</span>
        </div>
      </div>

      <div className='bx-price-match-review__header'>
        <h2 className='bx-price-match-review__title'>Review Submission</h2>
        <span
          className={`bx-price-match-review__status bx-price-match-review__status--${submission.status.toLowerCase()}`}
        >
          {getPriceMatchStatusLabel(submission.status)}
        </span>
      </div>

      <div className='bx-price-match-review__content'>
        <section className='bx-price-match-review__media-panel'>
          <div className='bx-price-match-preview'>
            <div ref={previewRef} className='bx-price-match-preview__frame' style={previewStyle}>
              {submission.imageUrl ? (
                <img
                  src={submission.imageUrl}
                  alt={submission.orderId}
                  className='bx-price-match-preview__image'
                />
              ) : (
                <PriceMatchPreviewGraphic />
              )}
            </div>
          </div>

          <div className='bx-price-match-review__controls'>
            <button
              type='button'
              className='bx-price-match-review__control'
              onClick={() => setPreviewScale((value) => (value === 1 ? 1.12 : 1))}
            >
              <span className='bx-price-match-review__control-icon'>
                <svg viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M11 18A7 7 0 1 0 11 4A7 7 0 0 0 11 18Z'
                    stroke='currentColor'
                    strokeWidth='1.8'
                  />
                  <path
                    d='M20 20L16.65 16.65'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                  />
                  <path
                    d='M11 8V14'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                  />
                  <path
                    d='M8 11H14'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                  />
                </svg>
              </span>
              Zoom
            </button>

            <button
              type='button'
              className='bx-price-match-review__control'
              onClick={() => setPreviewRotation((value) => (value + 90) % 360)}
            >
              <span className='bx-price-match-review__control-icon'>
                <svg viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M4 12A8 8 0 1 0 7.52 5.34'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                  />
                  <path
                    d='M4 4V10H10'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
              Rotate
            </button>

            <button
              type='button'
              className='bx-price-match-review__control'
              onClick={downloadPreview}
            >
              <span className='bx-price-match-review__control-icon'>
                <svg viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M12 16L12 6'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                  />
                  <path
                    d='M8 12L12 16L16 12'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <rect x='4' y='16' width='16' height='3' rx='1.5' fill='currentColor' />
                </svg>
              </span>
              Download
            </button>
          </div>
        </section>

        <section className='bx-price-match-review__details-panel'>
          {/* {showBanner && !isPending ? (
            <div
              className={`bx-price-match-review__banner bx-price-match-review__banner--${isApproved ? 'success' : 'error'}`}
            >
              <div className='bx-price-match-review__banner-icon'>
                {isApproved ? (
                  <svg viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
                      fill='#27AE60'
                    />
                    <path
                      d='M8 12L10.8 14.8L16.5 9.5'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                ) : (
                  <svg viewBox='0 0 24 24' fill='none'>
                    <circle cx='12' cy='12' r='10' fill='#FF0000' />
                    <path d='M9 9L15 15' stroke='white' strokeWidth='2' strokeLinecap='round' />
                    <path d='M15 9L9 15' stroke='white' strokeWidth='2' strokeLinecap='round' />
                  </svg>
                )}
              </div>
              <div className='bx-price-match-review__banner-copy'>
                {isApproved
                  ? 'Submission Approved! Wallet rebate has been credited successfully.'
                  : 'Submission Rejected! This submission could not be verified.'}
              </div>
              <button
                type='button'
                className='bx-price-match-review__banner-btn'
                onClick={() => setShowBanner(false)}
              >
                OK
              </button>
            </div>
          ) : null} */}

          <div className='bx-price-match-review__section'>
            <h3>Submission Details</h3>
            <div className='bx-price-match-review__card'>
              <div className='bx-price-match-review__row'>
                <span>Order ID</span>
                <strong>{submission.orderId}</strong>
              </div>
              <div className='bx-price-match-review__row'>
                <span>Customer</span>
                <strong>{submission.customer}</strong>
              </div>
              <div className='bx-price-match-review__row'>
                <span>Station Purchased From</span>
                <Link
                  to={`/boostxpress/stations/${station?.id}`}
                  className='text-dark text-decoration-none border-bottom'
                >
                  {station?.businessName || station?.branchName}, {station?.city}
                </Link>
              </div>
              <div className='bx-price-match-review__row'>
                <span>Product</span>
                <strong>{submission.product}</strong>
              </div>
              <div className='bx-price-match-review__row'>
                <span>Purchase Price</span>
                <strong>{submission.purchasePrice}</strong>
              </div>
              <div className='bx-price-match-review__row'>
                <span>Submitted</span>
                <strong>{submission.submittedAt}</strong>
              </div>
            </div>
          </div>

          {isPending ? (
            <>
              <div className='bx-price-match-review__section'>
                <h3>Additional Notes</h3>
                <div className='bx-price-match-review__note-card'>{submission.additionalNotes}</div>
              </div>

              <div className='bx-price-match-review__section'>
                <h3>Location Verification</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Capture Location</span>
                    <strong>{submission.captureLocation}</strong>
                  </div>
                  <div className='bx-price-match-review__row'>
                    <span>Distance From Boost Station:</span>
                    <strong>{submission.distanceFromBoost}</strong>
                  </div>
                </div>
              </div>

              <div className='bx-price-match-review__section'>
                <h3>Verification Checklist</h3>
                <div className='bx-price-match-review__checklist-card'>
                  {submission.checklist.map((item) => (
                    <label key={item.id} className='bx-price-match-review__checklist-item'>
                      <input
                        type='checkbox'
                        className='bx-price-match-review__check-input'
                        checked={item.passed}
                        readOnly
                      />
                      <span
                        className={`bx-price-match-review__check-icon ${
                          item.passed ? 'bx-price-match-review__check-icon--checked' : ''
                        }`}
                        aria-hidden='true'
                      >
                        <svg viewBox='0 0 24 24' fill='none'>
                          <rect x='3.5' y='3.5' width='17' height='17' rx='4.5' stroke='#27AE60' />
                          {item.passed ? (
                            <path
                              d='M8 12L10.75 14.75L16 9.5'
                              stroke='#27AE60'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          ) : null}
                        </svg>
                      </span>
                      {item.label}
                    </label>
                  ))}

                  {/* {submission.checklist.map((item) => (
                    <div key={item.id} className='bx-price-match-review__checklist-item'>
                      <span className='bx-price-match-review__check-icon'>
                        <svg viewBox='0 0 24 24' fill='none'>
                          <rect x='3.5' y='3.5' width='17' height='17' rx='4.5' stroke='#27AE60' />
                          <path
                            d='M8 12L10.75 14.75L16 9.5'
                            stroke='#27AE60'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </span>
                      {item.label}
                    </div>
                  ))} */}
                </div>
              </div>
            </>
          ) : null}

          {isApproved ? (
            <>
              <div className='bx-price-match-review__section'>
                <h3>Approval Details</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Approved by</span>
                    <strong>{submission.reviewedByAdminEmail}</strong>
                  </div>
                  <div className='bx-price-match-review__row'>
                    <span>Approval Date & Time</span>
                    <strong>{submission.approvalDateTime || formatReviewDate(new Date())}</strong>
                  </div>
                </div>
              </div>

              <div className='bx-price-match-review__section'>
                <h3>Wallet Credit Status</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Amount</span>
                    <strong>{submission.walletCreditAmount}</strong>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='bx-price-match-review__section'>
                <h3>Disapproval Details</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Disapproval by</span>
                    <strong>{submission.reviewedByAdminEmail}</strong>
                  </div>
                  <div className='bx-price-match-review__row'>
                    <span>Disapproval Date & Time</span>
                    <strong>{submission.reviewedAt || formatReviewDate(new Date())}</strong>
                  </div>
                </div>
              </div>

               <div className='bx-price-match-review__section'>
                <h3>Reason for Rejection</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Reason</span>
                    <strong>{submission.rejectionReason}</strong>
                  </div>
                  <div className='bx-price-match-review__row bx-price-match-review__row--notes'>
                    <span>Review Notes</span>
                    <strong>{submission.reviewNotes}</strong>
                  </div>
                </div>
              </div>

              {/* <div className='bx-price-match-review__section'>
                <h3>Wallet Credit Status</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Amount</span>
                    <strong>{submission.walletCreditAmount}</strong>
                  </div>
                </div>
              </div> */}
            </>
          )}

          {isDeclined ? (
            <>
              <div className='bx-price-match-review__section'>
                <h3>Rejection Details</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Rejected by</span>
                    <strong>{submission.rejectedBy}</strong>
                  </div>

                  <div className='bx-price-match-review__row'>
                    <span>Rejection note</span>
                    <strong>{submission.rejectionNote}</strong>
                  </div>

                  
                  <div className='bx-price-match-review__row'>
                    <span>Rejection Date & Time</span>
                    <strong>{submission.rejectionDateTime || formatReviewDate(new Date())}</strong>
                  </div>
                </div>
              </div>

              <div className='bx-price-match-review__section'>
                <h3>Reason for Rejection</h3>
                <div className='bx-price-match-review__card'>
                  <div className='bx-price-match-review__row'>
                    <span>Reason</span>
                    <strong>{submission.rejectionReason}</strong>
                  </div>
                  <div className='bx-price-match-review__row bx-price-match-review__row--notes'>
                    <span>Review Notes</span>
                    <strong>{submission.reviewNotes}</strong>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </section>
      </div>

      {isPending ? (
        <div className='bx-price-match-review__actions'>
          <button
            type='button'
            className='bx-price-match-review__action bx-price-match-review__action--reject'
            onClick={() => setShowRejectModal(true)}
          >
            Reject
          </button>
          <button
            type='button'
            className='bx-price-match-review__action bx-price-match-review__action--approve'
            onClick={() => setShowApproveModal(true)}
          >
            Approve & Credit Wallet
          </button>
        </div>
      ) : null}

      <ModalWrapper
        showModal={showApproveModal}
        closeModal={() => setShowApproveModal(false)}
        title='Approve Submission'
        hideHeader={false}
      >
        <div className='bx-price-match-modal bx-price-match-modal--approve pt-4'>
          {/* <div className='bx-price-match-modal__separator' /> */}
          <div>Approve this Price Match submission?</div>
          <div className='bx-price-match-modal__metric-box-'>
            <label style={{ fontSize: '.8rem' }}>Price Differential</label>
            <input
              type='number'
              className='form-control mt-1'
              value={approvePriceDifferenceMajor}
              onChange={(event) => setApprovePriceDifferenceMajor(event.target.value)}
              min={0}
              step='1'
              disabled={isSubmitting}
            />
          </div>
          <div className='bx-price-match-modal__actions-'>
            <button
              type='button'
              className='bx-price-match-modal__btn bx-price-match-modal__btn--primary px-3 w-100'
              onClick={handleApprove}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Confirming...' : 'Confirm Approval'}
            </button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper
        showModal={showRejectModal}
        closeModal={() => setShowRejectModal(false)}
        title='Reject Submission'
        hideHeader={false}
      >
        <div className='bx-price-match-modal bx-price-match-modal--reject mt-4'>
          <div className='bx-price-match-modal__field'>
            <label htmlFor='rejectionReason'>Reason for Rejection</label>
            <select
              id='rejectionReason'
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              disabled={isSubmitting}
            >
              {rejectionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className='bx-price-match-modal__field'>
            <label htmlFor='reviewNotes'>Review Notes</label>
            <textarea
              id='reviewNotes'
              rows={4}
              value={reviewNotes}
              onChange={(event) => setReviewNotes(event.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className='bx-price-match-modal__actions'>
            <button
              type='button'
              className='bx-price-match-modal__btn bx-price-match-modal__btn--primary w-100'
              onClick={handleReject}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm Rejection'}
            </button>
          </div>
        </div>
      </ModalWrapper>
    </div>
  )
}

export default PriceMatchDetails
